// src/store/slices/configuracionSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ConfiguracionService from 'services/ConfiguracionService';

export const fetchHorariosEmpresa = createAsyncThunk(
    'configuracion/fetchHorariosEmpresa',
    async (empresaId, { rejectWithValue }) => {
        try {
            return await ConfiguracionService.getHorariosEmpresa(empresaId);
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error al cargar los horarios');
        }
    }
);

/**
 * Configuración efectiva del empleado. Trae `valores` (lo que aplica
 * hoy), `origen` (propio | heredado | default, por campo) y `fuentes`.
 */
export const fetchConfigResuelta = createAsyncThunk(
    'configuracion/fetchResuelta',
    async (empleadoId, { rejectWithValue }) => {
        try {
            return await ConfiguracionService.getResuelta(empleadoId);
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error al resolver la configuración');
        }
    }
);

/**
 * Override propio del empleado (puede no existir todavía). Se carga
 * aparte de la resuelta porque el formulario edita el override, no el
 * valor efectivo: un campo vacío significa "hereda".
 */
export const fetchOverrideEmpleado = createAsyncThunk(
    'configuracion/fetchOverrideEmpleado',
    async (empleadoId, { rejectWithValue }) => {
        try {
            const res = await ConfiguracionService.getOverrideEmpleado(empleadoId);
            return res?.data?.[0] || null;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error al cargar el override');
        }
    }
);

export const guardarConfigEmpresa = createAsyncThunk(
    'configuracion/guardarEmpresa',
    async ({ empresaId, data }, { rejectWithValue }) => {
        try {
            return await ConfiguracionService.guardarEmpresa(empresaId, data);
        } catch (err) {
            return rejectWithValue(err.response?.data?.errors || err.response?.data?.message);
        }
    }
);

export const guardarConfigEmpleado = createAsyncThunk(
    'configuracion/guardarEmpleado',
    async ({ empleadoId, data }, { rejectWithValue }) => {
        try {
            return await ConfiguracionService.guardarEmpleado(empleadoId, data);
        } catch (err) {
            return rejectWithValue(err.response?.data?.errors || err.response?.data?.message);
        }
    }
);

export const createHorario = createAsyncThunk(
    'configuracion/createHorario',
    async (data, { rejectWithValue }) => {
        try {
            return await ConfiguracionService.create(data);
        } catch (err) {
            return rejectWithValue(err.response?.data?.errors || err.response?.data?.message);
        }
    }
);

export const updateHorario = createAsyncThunk(
    'configuracion/updateHorario',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            return await ConfiguracionService.update(id, data);
        } catch (err) {
            return rejectWithValue(err.response?.data?.errors || err.response?.data?.message);
        }
    }
);

export const deleteHorario = createAsyncThunk(
    'configuracion/deleteHorario',
    async (id, { rejectWithValue }) => {
        try {
            await ConfiguracionService.remove(id);
            return id;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Error al eliminar el horario');
        }
    }
);

const configuracionSlice = createSlice({
    name: 'configuracion',
    initialState: {
        // Configuraciones de nivel empresa. La primera (menor id) es la
        // que el backend usa como base heredable; el resto son turnos
        // asignables via empleados.horario_id.
        horarios: [],
        horariosLoading: false,

        resuelta: null,
        resueltaLoading: false,

        override: null,
        overrideLoading: false,

        saving: false,
        error: null,
    },
    reducers: {
        clearConfiguracionError: (state) => { state.error = null; },
        // Al cambiar de empleado hay que soltar lo anterior o el
        // formulario se pinta un instante con datos de otro.
        limpiarContextoEmpleado: (state) => {
            state.resuelta = null;
            state.override = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchHorariosEmpresa.pending, (state) => { state.horariosLoading = true; state.error = null; })
            .addCase(fetchHorariosEmpresa.fulfilled, (state, action) => {
                state.horariosLoading = false;
                state.horarios = action.payload.data || [];
            })
            .addCase(fetchHorariosEmpresa.rejected, (state, action) => {
                state.horariosLoading = false;
                state.horarios = [];
                state.error = action.payload;
            })

            .addCase(fetchConfigResuelta.pending, (state) => { state.resueltaLoading = true; })
            .addCase(fetchConfigResuelta.fulfilled, (state, action) => {
                state.resueltaLoading = false;
                state.resuelta = action.payload.data;
            })
            .addCase(fetchConfigResuelta.rejected, (state, action) => {
                state.resueltaLoading = false;
                state.resuelta = null;
                state.error = action.payload;
            })

            .addCase(fetchOverrideEmpleado.pending, (state) => { state.overrideLoading = true; })
            .addCase(fetchOverrideEmpleado.fulfilled, (state, action) => {
                state.overrideLoading = false;
                state.override = action.payload;
            })
            .addCase(fetchOverrideEmpleado.rejected, (state) => {
                state.overrideLoading = false;
                state.override = null;
            })

            .addCase(guardarConfigEmpresa.pending, (state) => { state.saving = true; })
            .addCase(guardarConfigEmpresa.fulfilled, (state) => { state.saving = false; })
            .addCase(guardarConfigEmpresa.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload;
            })

            .addCase(guardarConfigEmpleado.pending, (state) => { state.saving = true; })
            .addCase(guardarConfigEmpleado.fulfilled, (state) => { state.saving = false; })
            .addCase(guardarConfigEmpleado.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload;
            })

            .addCase(createHorario.pending, (state) => { state.saving = true; })
            .addCase(createHorario.fulfilled, (state) => { state.saving = false; })
            .addCase(createHorario.rejected, (state, action) => { state.saving = false; state.error = action.payload; })

            .addCase(updateHorario.pending, (state) => { state.saving = true; })
            .addCase(updateHorario.fulfilled, (state) => { state.saving = false; })
            .addCase(updateHorario.rejected, (state, action) => { state.saving = false; state.error = action.payload; })

            .addCase(deleteHorario.fulfilled, (state, action) => {
                state.horarios = state.horarios.filter((h) => h.id !== action.payload);
            });
    },
});

export const { clearConfiguracionError, limpiarContextoEmpleado } = configuracionSlice.actions;
export default configuracionSlice.reducer;
